import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Booking review dialog. Renders a 1–5 star selector + short comment.
 * The parent controls open state via `open` / `onOpenChange`, and gets a
 * callback via `onSubmitted(review)` after a successful POST.
 */
export default function ReviewForm({ booking, open, onOpenChange, onSubmitted }) {
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => { setRating(5); setHover(0); setComment(""); };

  const submit = async () => {
    if (!comment.trim() || comment.trim().length < 3) {
      return toast.error("Add a short comment (min 3 characters)");
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/reviews`, {
        booking_id: booking.id, rating, comment: comment.trim(),
      });
      toast.success("Thanks for your review!");
      reset();
      onOpenChange(false);
      onSubmitted?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit review");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle>Rate your stay at {booking?.property_title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Overall rating</div>
            <div className="mt-2 flex items-center gap-1" role="radiogroup" aria-label="Star rating">
              {[1, 2, 3, 4, 5].map((n) => {
                const active = (hover || rating) >= n;
                return (
                  <button
                    key={n}
                    type="button"
                    data-testid={`review-star-${n}`}
                    role="radio"
                    aria-checked={rating === n}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(n)}
                    className="p-1 outline-none focus-visible:ring-2 focus-visible:ring-copper rounded"
                  >
                    <Star
                      className={`h-8 w-8 transition ${active ? "fill-copper text-copper" : "text-slate-300"}`}
                      strokeWidth={active ? 1.5 : 2}
                    />
                  </button>
                );
              })}
              <span className="ml-2 text-sm font-semibold text-slate-700">{rating}/5</span>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500" htmlFor="review-comment">Comment</label>
            <textarea
              id="review-comment"
              data-testid="review-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, 800))}
              placeholder="Was the host responsive? Was the space clean? Would you rebook?"
              rows={4}
              className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
            />
            <div className="mt-1 text-right text-xs text-slate-400">{comment.length}/800</div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="btn-outline text-sm" data-testid="review-cancel-btn">Cancel</button>
          <button onClick={submit} disabled={saving} className="btn-primary text-sm" data-testid="review-submit-btn">
            {saving ? "Sending…" : "Submit review"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
